if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('mini-cart');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonLabel = this.submitButton?.querySelector('.product-form__submit-label');
        this.submitButtonPrice = this.submitButton?.querySelector('.product-form__submit-price');
        this.submitButtonText = this.submitButtonLabel || this.submitButton?.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton?.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this._variantChangeUnsub = undefined;
        this._cartUpdateUnsub = undefined;
        this._quantityInput = undefined;
        this._onQtyChange = undefined;
      }

      connectedCallback() {
        if (!this.submitButtonPrice || !this.dataset.sectionId) return;

        this._currentVariant = this.readInitialVariantFromJson();

        this._variantChangeUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.dataset.sectionId) return;
          this._currentVariant = event.data.variant ?? null;
          this.updateSubmitLinePrice();
        });

        this._cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          this.updateSubmitLinePrice(event);
        });

        const qid = `Quantity-${this.dataset.sectionId}`;
        this._quantityInput = document.getElementById(qid);
        this._onQtyChange = () => this.updateSubmitLinePrice();
        this._quantityInput?.addEventListener('change', this._onQtyChange);
        this._quantityInput?.addEventListener('input', this._onQtyChange);

        this.updateSubmitLinePrice();
      }

      disconnectedCallback() {
        this._variantChangeUnsub?.();
        this._cartUpdateUnsub?.();
        if (this._quantityInput && this._onQtyChange) {
          this._quantityInput.removeEventListener('change', this._onQtyChange);
          this._quantityInput.removeEventListener('input', this._onQtyChange);
        }
      }

      readInitialVariantFromJson() {
        const el = this.querySelector('.js-product-form-variant-json');
        if (!el?.textContent?.trim()) return null;
        try {
          return JSON.parse(el.textContent);
        } catch {
          return null;
        }
      }

      setSubmitLabelText(text) {
        if (this.submitButtonLabel) {
          this.submitButtonLabel.textContent = text;
        } else if (this.submitButtonText) {
          this.submitButtonText.textContent = text;
        } else if (this.submitButton) {
          this.submitButton.textContent = text;
        }
      }

      effectiveUnitCentsFromVariant(variant, volQty) {
        if (!variant) return 0;
        let unitCents = variant.price;
        const breaks = variant.quantity_price_breaks;
        if (breaks && breaks.length > 0) {
          const sorted = [...breaks].sort((a, b) => b.minimum_quantity - a.minimum_quantity);
          const minThreshold = sorted[sorted.length - 1].minimum_quantity;
          if (volQty >= minThreshold) {
            for (let i = 0; i < sorted.length; i++) {
              if (volQty >= sorted[i].minimum_quantity) {
                unitCents = sorted[i].price;
                break;
              }
            }
          }
        }
        return unitCents;
      }

      resolveCartQtyFromEvent(event, variantId) {
        if (!event?.cart || variantId == null) return null;
        const c = event.cart;
        const idStr = String(variantId);
        if (Array.isArray(c.items)) {
          const item = c.items.find((i) => String(i.variant_id) === idStr);
          return item ? item.quantity : 0;
        }
        if (c.variant_id != null && String(c.variant_id) === idStr && c.quantity != null) {
          return parseInt(c.quantity, 10);
        }
        return null;
      }

      refreshSubmitAriaLabel(formattedPrice) {
        if (!this.submitButton || !this.submitButtonPrice) return;
        if (this.submitButtonPrice.classList.contains('hidden') || !this.submitButtonPrice.textContent.trim()) {
          if (!this.submitButton.hasAttribute('disabled')) {
            this.submitButton.removeAttribute('aria-label');
          }
          return;
        }
        const price = formattedPrice ?? this.submitButtonPrice.textContent.trim();
        const label =
          (this.submitButtonLabel?.textContent?.trim() ||
            this.submitButtonText?.textContent?.trim() ||
            '') + '';
        if (!price || !label) return;
        const fmt = theme.productStrings?.formatSubmitButtonAriaWithPrice;
        this.submitButton.setAttribute(
          'aria-label',
          fmt ? fmt(label, price) : `${label}, ${price}`
        );
      }

      updateSubmitLinePrice(cartEvent) {
        if (!this.submitButtonPrice || !this.dataset.sectionId) return;

        const variant = this._currentVariant;

        if (!variant || variant.available === false) {
          this.submitButtonPrice.classList.add('hidden');
          this.submitButtonPrice.textContent = '';
          if (this.submitButton && !this.submitButton.hasAttribute('disabled')) {
            this.submitButton.removeAttribute('aria-label');
          }
          return;
        }

        const sectionId = this.dataset.sectionId;
        const qtyInput = document.getElementById(`Quantity-${sectionId}`);

        let cartQty = 0;
        if (qtyInput) {
          cartQty = parseInt(qtyInput.dataset.cartQuantity || '0', 10) || 0;
        }
        const fromEvent = this.resolveCartQtyFromEvent(cartEvent, variant.id);
        if (fromEvent !== null) {
          cartQty = fromEvent;
        }

        let enteredQty = 1;
        if (qtyInput) {
          enteredQty = parseInt(qtyInput.value, 10);
          if (Number.isNaN(enteredQty) || enteredQty < 1) {
            enteredQty = parseInt(qtyInput.getAttribute('min'), 10) || 1;
          }
        }

        const volQty = cartQty + enteredQty;
        const unitCents = this.effectiveUnitCentsFromVariant(variant, volQty);
        const lineTotal = unitCents * enteredQty;
        const formatted = theme.Currency.formatMoney(lineTotal, theme.shopSettings.moneyFormat);

        this.submitButtonPrice.textContent = formatted;
        this.submitButtonPrice.classList.remove('hidden');
        this.refreshSubmitAriaLabel(formatted);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (!this.blockSubmitIfRequiredFieldsEmpty(evt)) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${theme.routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              const labelNode = this.submitButtonText || this.submitButtonLabel;
              if (labelNode) labelNode.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = theme.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cart: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          });
      }

      blockSubmitIfRequiredFieldsEmpty(event) {
        if (!(this.form instanceof HTMLFormElement)) {
          throw new Error('Expected a form element.');
        }

        const formId = this.form.getAttribute('id');
        const requiredFields = document.querySelectorAll('[required]');
        let allFilled = true;
        for (const field of requiredFields) {
          const belongsToForm = field.getAttribute('form') === formId || field.closest('form')?.id === formId;
          if (!belongsToForm) continue;

          let isValid = true;
          switch (field.type) {
            case 'checkbox':
            case 'radio': {
              const group = document.querySelectorAll(`input[name="${field.name}"][type="${field.type}"]`);
              const isChecked = Array.from(group).some((input) => input.checked);
              isValid = isChecked;
              break;
            }
            case 'select-one':
              isValid = field.value !== '';
              break;
            case 'textarea':
            case 'text':
            case 'email':
            case 'number':
            case 'url':
            default:
              isValid = field.value.trim() !== '';
              break;
          }
          if (!isValid) {
            allFilled = false;
            field.classList.add('invalid');
          } else {
            field.classList.remove('invalid');
          }
        }

        if (!allFilled) {
          event.preventDefault();
          this.handleErrorMessage(theme.accessibilityStrings.fillInAllLineItemPropertyRequiredFields);
          return false;
        }
        return true;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const labelTarget = this.submitButtonLabel || this.submitButtonText;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) {
            if (labelTarget) labelTarget.textContent = text;
            else if (this.submitButton) this.submitButton.textContent = text;
          }
          this.submitButtonPrice?.classList.add('hidden');
          this.submitButton.removeAttribute('aria-label');
        } else {
          this.submitButton.removeAttribute('disabled');
          const nextLabel =
            this.submitButton.dataset.preOrder === 'true'
              ? theme.variantStrings.preOrder
              : theme.variantStrings.addToCart;
          this.setSubmitLabelText(nextLabel);
          if (this.submitButtonPrice && this.dataset.sectionId) {
            this.updateSubmitLinePrice();
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
